// src/utils/editorExtensions.js
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { BubbleMenu } from "@tiptap/extension-bubble-menu";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { Hashtag } from "../extensions/Hashtag";
// Import the CodeBlockLowlight extension
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
// Import lowlight with correct path - FIXED IMPORT
import { common, createLowlight } from "lowlight";
// Create a lowlight instance
const lowlight = createLowlight(common);
// Import specific languages
import javascript from "highlight.js/lib/languages/javascript";
import typescript from "highlight.js/lib/languages/typescript";
import html from "highlight.js/lib/languages/xml";
import css from "highlight.js/lib/languages/css";
import python from "highlight.js/lib/languages/python";
import json from "highlight.js/lib/languages/json";
import bash from "highlight.js/lib/languages/bash";
import markdown from "highlight.js/lib/languages/markdown";

// Register languages you want to support
lowlight.register("javascript", javascript);
lowlight.register("typescript", typescript);
lowlight.register("html", html);
lowlight.register("css", css);
lowlight.register("python", python);
lowlight.register("json", json);
lowlight.register("bash", bash);
lowlight.register("md", markdown);

/**
 * Creates and configures all editor extensions
 *
 * @param {HTMLElement} bubbleMenuElement - The DOM element for the bubble menu
 * @param {Function} hashtagClickHandler - The function to handle hashtag clicks
 * @returns {Array} Array of configured TipTap extensions
 */
export function createEditorExtensions(bubbleMenuElement, hashtagClickHandler) {
  return [
    StarterKit.configure({
      // Configure StarterKit to use minimal spacing
      paragraph: {
        HTMLAttributes: {
          class: "editor-paragraph",
        },
      },
      // Disable the default code block as we'll replace it
      codeBlock: false,
    }),
    // Add syntax highlighting for code blocks
    CodeBlockLowlight.configure({
      lowlight,
      HTMLAttributes: {
        class: "code-block",
      },
      languageClassPrefix: "language-",
    }),
    Link.configure({
      openOnClick: true,
      HTMLAttributes: {
        class: "underline",
      },
    }),
    Image.configure({
      HTMLAttributes: {
        class: "max-w-full h-auto",
      },
    }),
    Placeholder.configure({
      placeholder: "Type something...",
      emptyEditorClass: "is-editor-empty",
      emptyNodeClass: "is-empty",
    }),
    // Add TaskList and TaskItem extensions
    TaskList.configure({
      HTMLAttributes: {
        class: "task-list",
      },
    }),
    TaskItem.configure({
      nested: true,
      HTMLAttributes: {
        class: "task-item",
      },
    }),
    BubbleMenu.configure({
      element: bubbleMenuElement,
      shouldShow: ({ editor, view, state, from, to }) => {
        return !editor.isActive("codeBlock") && !state.selection.empty;
      },
    }),
    // Add the Hashtag extension with configured styles and handlers
    Hashtag.configure({
      HTMLAttributes: {
        class: "hashtag",
        onClick: (event) => {
          // Extract hashtag from the clicked element
          const hashtag =
            event.target.getAttribute("data-id") ||
            event.target.textContent.substring(1); // Remove the # character

          hashtagClickHandler(hashtag);
          event.preventDefault();
          event.stopPropagation();
        },
      },
    }),
  ];
}

/**
 * Create editor configuration object with all options
 *
 * @param {HTMLElement} element - The DOM element for the editor
 * @param {HTMLElement} bubbleMenuElement - The DOM element for the bubble menu
 * @param {string} content - Initial editor content
 * @param {Function} hashtagClickHandler - Function to handle hashtag clicks
 * @param {Function} onUpdateHandler - Handler for content updates
 * @param {Function} onSelectionUpdateHandler - Handler for selection updates
 * @param {Function} onBlurHandler - Handler for blur events
 * @returns {Object} Editor configuration object
 */
export function createEditorConfig(
  element,
  bubbleMenuElement,
  content,
  hashtagClickHandler,
  onUpdateHandler,
  onSelectionUpdateHandler,
  onBlurHandler,
) {
  return {
    element,
    extensions: createEditorExtensions(bubbleMenuElement, hashtagClickHandler),
    content: content || "",
    autofocus: false,
    onUpdate: onUpdateHandler,
    onSelectionUpdate: onSelectionUpdateHandler,
    onBlur: onBlurHandler,
  };
}

/**
 * Helper to determine if selection should show bubble menu
 *
 * @param {Object} editor - TipTap editor instance
 * @returns {boolean} True if bubble menu should be shown
 */
export function shouldShowBubbleMenu(editor) {
  if (!editor) return false;

  return !editor.isActive("codeBlock") && !editor.state.selection.empty;
}

/**
 * Add custom keyboard shortcuts to the editor
 *
 * @param {Object} editor - TipTap editor instance
 * @param {Function} dispatch - Svelte dispatch function
 */
export function addCustomKeyboardShortcuts(editor, dispatch) {
  if (!editor) return;

  // Add custom keyboard event handler
  const element = editor.view.dom;
  element.addEventListener("keydown", (event) => {
    // Pass keyboard events up to parent
    dispatch("keydown", event);

    // Add any custom shortcut handling here
  });
}

/**
 * Create a complete editor with all configurations and handlers
 *
 * @param {HTMLElement} editorElement - The DOM element for the editor
 * @param {HTMLElement} bubbleMenuElement - The DOM element for the bubble menu
 * @param {Object} block - Block data object
 * @param {Function} saveSelection - Function to save selection
 * @param {Function} dispatchFunction - Svelte dispatch function
 * @param {Function} updateHashtags - Function to update hashtags array
 * @returns {Object} Configured TipTap editor instance
 */
export function createCompleteEditor(
  editorElement,
  bubbleMenuElement,
  block,
  saveSelection,
  dispatchFunction,
  updateHashtags,
) {
  // Import necessary functions
  const { Editor } = require("@tiptap/core");
  const { syncContentWithDebounce } = require("./editorHelpers");

  // Create hashtag click handler
  const handleHashtagClick = (hashtag) => {
    dispatchFunction("hashtagClick", {
      hashtag,
      blockId: block.id,
    });
  };

  // Create editor config
  const config = createEditorConfig(
    editorElement,
    bubbleMenuElement,
    block.content,
    handleHashtagClick,
    // Update handler
    () => {
      syncContentWithDebounce(
        editor,
        block,
        dispatchFunction,
        [],
        updateHashtags,
      );
    },
    // Selection update handler
    () => {
      saveSelection(editorElement);
    },
    // Blur handler
    () => {
      syncContentWithDebounce(
        editor,
        block,
        dispatchFunction,
        [],
        updateHashtags,
      );
    },
  );
}
